import { truncate } from 'lodash';
import {
  sqDurationStore,
  sqWorkbenchStore,
  sqWorkbookStore,
  sqWorksheetStore,
  sqWorkstepsStore,
} from '@/core/core.stores';
import { formatTime } from '@/datetime/dateTime.utilities';
import { getViewWorksheetLink, goToWithReload } from '@/main/routing.utilities';
import { hasAnalyticsAdminCapability } from '@/services/authorization.service';
import { capturesEnabled, genAIEnabled, genAIImageUploadsEnabled } from '@/services/systemConfiguration.utilities';
import { getBrowserUrl } from '@/utilities/browserLocation.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { isEditWorkbookMode, isViewOnlyWorkbookMode } from '@/utilities/workbookMode.utilities';
import { setWorkbookDisplayMode } from '@/workbook/workbook.actions';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import { sqCapturesApi } from '@seeqdev/api/endpoint/CapturesApi';
import { ViewTypeEnum } from '@seeqdev/api/model/CaptureInputV1';
import type { Moment } from 'moment';
import type { CaptureShareDraft, EditableCapture } from '@/captures/captures.types';
import type { WorksheetViewType } from '@/worksheet/worksheet.constants';
import type { CaptureDisplayedRangeV1 } from '@seeqdev/api/model/CaptureDisplayedRangeV1';
import type { CaptureInputV1 } from '@seeqdev/api/model/CaptureInputV1';
import type { CaptureOutputV1 } from '@seeqdev/api/model/CaptureOutputV1';
import type { CapturePreviewV1 } from '@seeqdev/api/model/CapturePreviewV1';
import type { CaptureShareInputV1 } from '@seeqdev/api/model/CaptureShareInputV1';

/** The query parameter a link names the capture to open by. */
export const CAPTURE_ID_PARAM = 'captureId';

/** Everything a capture records about where it was taken. */
export type CaptureSource = Omit<CaptureInputV1, 'description'>;

/** The title a capture carries when nobody types one. */
export function capturePlaceholderTitle(workbookName: string, worksheetName?: string): string {
  return worksheetName ? `${workbookName} - ${worksheetName}` : workbookName;
}

/** Whether the capture dialog offers to generate a title and description, which sends the picture to a model. */
export function captureSuggestionsEnabled(): boolean {
  return genAIEnabled() === true && genAIImageUploadsEnabled() === true;
}

/** Whether a capture still carries the placeholder title of the worksheet it was taken on, so nobody named it. */
export function hasPlaceholderTitle({ title, worksheetId }: CaptureOutputV1): boolean {
  return title === capturePlaceholderTitle(sqWorkbookStore.name, sqWorkbookStore.getWorksheetName(worksheetId));
}

/** The CAPTURE-n a person refers to a capture by. */
export function captureLabel(number: number): string {
  return `CAPTURE-${number}`;
}

const STAMP_DATE_FORMAT = 'MMM D YYYY';
const STAMP_DATE_TIME_FORMAT = 'MMM D YYYY HH:mm';

/** The day a capture was taken, as the picture's stamp writes it in the worksheet's time zone. */
export function captureStampDate(time: Moment | number | string, timezone: { name: string }): string {
  return formatTime(time, timezone, STAMP_DATE_FORMAT);
}

/** The visible time range the picture's stamp carries, in the time zone the chart's x axis uses. */
export function captureStampRange(
  range: { start: Moment | number; end: Moment | number },
  timezone: { name: string },
): { start: string; end: string; zone: string } {
  return {
    start: formatTime(range.start, timezone, STAMP_DATE_TIME_FORMAT),
    end: formatTime(range.end, timezone, STAMP_DATE_TIME_FORMAT),
    zone: formatTime(range.end, timezone, 'z'),
  };
}

/** The `viewType` a capture from this view carries, or undefined when the view cannot be captured. */
export function captureViewType(viewKey: WorksheetViewType): ViewTypeEnum | undefined {
  switch (viewKey) {
    case WORKSHEET_VIEW.TREND:
      return ViewTypeEnum.Trend;
    case WORKSHEET_VIEW.SCATTER_PLOT:
      return ViewTypeEnum.Scatter;
    case WORKSHEET_VIEW.TABLE:
      return ViewTypeEnum.Table;
    case WORKSHEET_VIEW.TREEMAP:
      return ViewTypeEnum.Treemap;
    default:
      return undefined;
  }
}

/**
 * Where a capture taken right now would come from, or undefined when this worksheet cannot be captured. The stores
 * are read on the call, so the answer describes the worksheet at the moment of the press.
 */
export function currentCaptureSource(): CaptureSource | undefined {
  const viewType = captureViewType(sqWorksheetStore.view.key);
  const workstepId = sqWorkstepsStore.getState().current.id;

  if (!viewType || !workstepId) {
    return undefined;
  }

  const { workbookId, worksheetId } = sqWorkbenchStore.stateParams;

  return {
    workbookId,
    worksheetId,
    workstepId,
    viewType,
    title: capturePlaceholderTitle(sqWorkbookStore.name, sqWorkbookStore.getWorksheetName(worksheetId)),
  };
}

/**
 * Whether the signed-in user can edit this capture. Only its creator may write it, only while it is not deleted, and
 * only a capture whose picture was saved has the layout an edit restores.
 */
export function canEditCapture(capture: CaptureOutputV1): capture is EditableCapture {
  return !capture.archived && capture.layout !== undefined && capture.creator?.id === sqWorkbenchStore.currentUser.id;
}

/** Matches the server's delete rule: the creator or an analytics administrator. */
export const canDeleteCapture = (capture: Pick<CapturePreviewV1, 'creator'>): boolean =>
  capture.creator?.id === sqWorkbenchStore.currentUser.id || hasAnalyticsAdminCapability();

/** How much of a title the delete toast names, so a long one cannot stretch the toast down the page. */
const DELETED_TITLE_LENGTH = 60;

/** Says which capture was deleted and offers to restore it, calling [onRestored] once it is back. */
export function captureDeletedToast(
  { id, title }: Pick<CapturePreviewV1, 'id' | 'title'>,
  onRestored: () => void,
): void {
  successToast({
    messageKey: 'CAPTURES.MANAGEMENT.DELETED_V2',
    messageParams: { title: truncate(title, { length: DELETED_TITLE_LENGTH }) },
    toastUUID: `CAPTURE_DELETE_${id}`,
    buttonId: `restoreCaptureBtn_${id}`,
    buttonLabelKey: 'RESTORE',
    buttonAction: () =>
      void sqCapturesApi.unarchiveCapture({ id }).then(
        () => {
          successToast({ messageKey: 'CAPTURES.MANAGEMENT.RESTORED' });
          onRestored();
        },
        (error: unknown) => errorToast({ httpResponseOrError: error }),
      ),
  });
}

/** Whether the chart shows exactly the range a capture's picture was taken of. */
export function isOnCaptureWindow(range: CaptureDisplayedRangeV1): boolean {
  const { start, end } = sqDurationStore.getState().displayRange;
  return start === Date.parse(range.start) && end === Date.parse(range.end);
}

/** Whether the captures management page and the links to it exist for this user. */
export const shouldOfferCapturesManagement = (isTrusted: boolean): boolean => capturesEnabled() && isTrusted;

/** The message naming what a share still needs before it can be sent. */
export type CaptureShareNeed = 'CAPTURES.SHARE.NEEDS_RECIPIENT' | 'CAPTURES.SHARE.NEEDS_SUBJECT';

/** What a share still needs before it can be sent, or undefined once it is ready. */
export function captureShareNeeds(share: CaptureShareDraft, subject: string): CaptureShareNeed | undefined {
  if (share.recipients.length === 0) {
    return 'CAPTURES.SHARE.NEEDS_RECIPIENT';
  }

  if (subject.trim() === '') {
    return 'CAPTURES.SHARE.NEEDS_SUBJECT';
  }

  return undefined;
}

/** The share a draft sends, with the subject and body as the fields show them. */
export function captureShareInput(draft: CaptureShareDraft, subject: string, body: string): CaptureShareInputV1 {
  return { subject, body, targets: draft.recipients.map(({ id }) => ({ identityId: id })) };
}

/** Where a capture lives, which is all its link needs. */
export type CaptureLocation = {
  id: string;
  workbookId: string;
  worksheetId: string;
  workstepId: string;
};

/**
 * The link to a capture: its pinned workstep in view mode, naming the capture so the worksheet can open it. Every
 * entry point builds the link here so they all land in the same place.
 */
export function captureLink({ id, workbookId, worksheetId, workstepId }: CaptureLocation): string {
  const params = new URLSearchParams({ workstepId, [CAPTURE_ID_PARAM]: id });
  return `${getViewWorksheetLink(workbookId, worksheetId)}?${params.toString()}`;
}

export function captureWorkstepLink({ workbookId, worksheetId, workstepId }: Omit<CaptureLocation, 'id'>): string {
  return `${getViewWorksheetLink(workbookId, worksheetId)}?${new URLSearchParams({ workstepId }).toString()}`;
}

/** Loads the link afresh in view mode, since the worksheet can't switch from edit to view mode in place. */
export function reloadInViewMode(link: string) {
  if (isEditWorkbookMode()) {
    setWorkbookDisplayMode(WORKBOOK_DISPLAY.VIEW);
  }

  goToWithReload(link);
}

export function isViewingCaptureWorkstep({ workstepId }: Pick<CaptureLocation, 'workstepId'>): boolean {
  return isViewOnlyWorkbookMode() && sqWorkstepsStore.getState().current.id?.toUpperCase() === workstepId.toUpperCase();
}

/** The capture's link with the server's address in front, for pasting outside Seeq. */
export function absoluteCaptureLink(location: CaptureLocation): string {
  return getBrowserUrl().origin + captureLink(location);
}
